// ProdStatDashboard.tsx
import React, { useState, useEffect } from 'react';
import { Table, Select, Alert, Tooltip, App, Checkbox } from 'antd';
import type { ButtonProps } from 'antd';
import ExportToExcelButton from '@components/ui/ExportToExcelButton';
import type { ColumnsType } from 'antd/es/table';
import { toTestAttrValue, withTestColumnAttrs, type TestAttrs } from '@utils/testAttr';
import { Link } from 'react-router-dom';
import styles from './ProdStatDashboard.module.css';
import useProdStatDashboard from './useProdStatDashboard';
import PageLayout from '@components/layout/PageLayout';
import { NSPButton } from '@components/ui/NSPButton';
import { NSPDatePicker } from '@components/ui/NSPDatepicker';
import ApprovalSignatureCell, { ApprovalColumnHeader } from '@components/ui/ApprovalSignatureCell';
import EstimationBlade from '@components/blades/estimation-blade/EstimationBlade';
import { useEstimationBlade } from '@components/blades/estimation-blade/useEstimationBlade';

type Row = {
  meter_computed_id: number;
  location: string | null;
  vendor_name?: string | null;
  meter_id: string | number;
  meter_number: string | null;
  units_delivered: number | null;
  units_consumed: number | null;
  prod_stat_netgen: number | null;
  percent_change_net_units: number | null;
  meter_read_type: string | null;
  comments?: string | null;
  approval_status?: string | null;
  ready_for_approval?: string | null;
  prod_stat_approval_signature?: string | null;
  manual_adj_for_netgen?: number | null;
  manual_kwh_adjustment?: number | null;
  manual_kwh_adjustment_comments?: string | null;
};

const formatPercentChange = (v: number | string | null | undefined) => {
  if (v == null || Number.isNaN(Number(v))) return '0.00%';
  return `${Number(v).toFixed(2)}%`;
};

const ProdStatDashboard = () => {
  const {
    monthYearLabel,
    isNextDisabled,
    pickerValue,
    selectedPeriod,
    navigateMonth,
    handleDateChange,
    data,
    loading,
    isAdmin,
    isSuperAdmin,
    isManager,
    isUser,
    saving,
    handleEdit,
    handleSave,
    saveFeedback,
    asyncApproval,
    setAsyncApproval,
    username,
    handleExportToExcel,
    getBulkApproveHeaderState,
    handleBulkApproveHeaderChange,
    hasPendingApprovals,
    isSaveDisabled,
    isRowOriginallyApproved,
    getBulkReadyHeaderState,
    handleBulkReadyHeaderChange,
  } = useProdStatDashboard();

  const { modal } = App.useApp();

  const [selectedMeter, setSelectedMeter] = useState<Row | null>(null);
  const [bladeMonthYear, setBladeMonthYear] = useState(selectedPeriod);
  useEffect(() => { setBladeMonthYear(selectedPeriod); }, [selectedPeriod]);

  const bladeOpen = !!selectedMeter;

  const {
    meterData,
    energySource,
    regionalData,
    saving: bladeSaving,
    applyError,
    handleApply,
  } = useEstimationBlade({
    open: bladeOpen,
    meter: selectedMeter,
    monthYear: bladeMonthYear,
    onSuccess: () => setSelectedMeter(null),
  });

  // Only admins and managers can approve
  const canApprove = isAdmin || isSuperAdmin || isManager;
  // Preparers can mark ready for approval
  const canMarkReady = isUser || isSuperAdmin;

  // Derived state for the approver bulk header checkbox
  const { checked: bulkHeaderChecked, indeterminate: bulkHeaderIndeterminate, disabled: bulkHeaderDisabled } =
    getBulkApproveHeaderState();

  // Derived state for the preparer bulk ready header checkbox
  const { checked: bulkReadyChecked, indeterminate: bulkReadyIndeterminate, disabled: bulkReadyDisabled } =
    getBulkReadyHeaderState();

  const handleSaveWithConfirm = () => {
    if (hasPendingApprovals) {
      modal.confirm({
        title: 'Estimation factors will become read only',
        content: 'The estimation factors for this month will now become read only. Do you want to continue?',
        okText: 'Save',
        cancelText: 'Cancel',
        okButtonProps: { 'data-testid': 'modal-ok-button' } as ButtonProps,
        cancelButtonProps: { 'data-testid': 'modal-cancel-button' } as ButtonProps,
        onOk: handleSave,
      });
    } else {
      handleSave();
    }
  };

  const bulkReadyTooltip = bulkReadyDisabled ? 'All meters are ready' : bulkReadyChecked ? 'Unmark all as ready' : 'Mark all as ready for approval';

  const columns: ColumnsType<Row> = [
    // Bulk "Ready for Approval" column — preparer only
    ...(canMarkReady ? [{
      title: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Tooltip title={bulkReadyTooltip}>
            <Checkbox
              checked={bulkReadyChecked}
              indeterminate={bulkReadyIndeterminate}
              disabled={bulkReadyDisabled}
              onChange={(e) => handleBulkReadyHeaderChange(e.target.checked)}
              data-testid="bulk-ready-checkbox"
              data-tooltip={bulkReadyTooltip}
            />
          </Tooltip>
          <span>Ready</span>
        </div>
      ),
      key: 'bulk_ready',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, row: Row) => {
        const isPersisted = isRowOriginallyApproved(row.meter_computed_id);
        const isReady = row.ready_for_approval === 'Ready for Approval';
        return (
          <Checkbox
            checked={isReady}
            disabled={isPersisted}
            onChange={(e) => {
              const val = e.target.checked ? 'Ready for Approval' : 'In Progress';
              handleEdit(row.meter_computed_id, 'ready_for_approval', val);
              if (!e.target.checked) {
                handleEdit(row.meter_computed_id, 'approval_status', '');
                handleEdit(row.meter_computed_id, 'prod_stat_approval_signature', '');
              }
            }}
            data-testid="ready-checkbox"
          />
        );
      },
    }] as ColumnsType<Row> : []),
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      width: 80,
      ellipsis: true,
      fixed: 'left',
      sorter: (a, b) => (a.vendor_name ?? '').localeCompare(b.vendor_name ?? ''),
      sortDirections: ['ascend', 'descend', null],
      onHeaderCell: () => ({ className: styles.vendorHeader }),
      onCell: () => ({ className: styles.vendorCell }),
    },
    {
      title: 'Name',
      dataIndex: 'location',
      key: 'location',
      width: 80,
      ellipsis: true,
      fixed: 'left',
      sorter: (a, b) => (a.location ?? '').localeCompare(b.location ?? ''),
      sortDirections: ['ascend', 'descend', null],
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Meter Id',
      dataIndex: 'meter_number',
      key: 'meter_number',
      width: 130,
      fixed: 'left',
      sorter: (a, b) =>
        (a.meter_number ?? '').toString().localeCompare((b.meter_number ?? '').toString()),
      sortDirections: ['ascend', 'descend', null],
      render: (_: any, record: Row) => (
        <Link to={`/meterreaddetails/${record.meter_id}`} data-testid="meter-number-link">{record.meter_number}</Link>
      ),
    },
    {
      title: 'Gen kWh',
      dataIndex: 'units_delivered',
      key: 'units_delivered',
      width: 95,
      render: (v: number | null) =>
        v == null ? '0' : Math.round(v).toLocaleString(undefined),
      sorter: (a, b) => (a.units_delivered ?? 0) - (b.units_delivered ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: 'Used kWh',
      dataIndex: 'units_consumed',
      key: 'units_consumed',
      width: 100,
      render: (v: number | null) =>
        v == null ? '0' : Math.round(v).toLocaleString(undefined),
      sorter: (a, b) => (a.units_consumed ?? 0) - (b.units_consumed ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: 'Adj kWh',
      dataIndex: 'manual_kwh_adjustment',
      key: 'manual_kwh_adjustment',
      width: 95,
      render: (_: unknown, record: Row) => {
        const rawValue = record.manual_kwh_adjustment;
        const formattedValue =
          rawValue == null ? '0' : Math.round(rawValue).toLocaleString(undefined);
        const comment = record.manual_kwh_adjustment_comments;
        if (comment && comment.trim() !== '') {
          return (
            <Tooltip title={comment} placement="topLeft">
              <span data-tooltip={comment}>{formattedValue}</span>
            </Tooltip>
          );
        }
        return formattedValue;
      },
      sorter: (a, b) => (a.manual_kwh_adjustment ?? 0) - (b.manual_kwh_adjustment ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: 'Net Gen kWh',
      dataIndex: 'prod_stat_netgen',
      key: 'prod_stat_netgen',
      width: 125,
      render: (v: number | null) =>
        v == null ? '0' : Math.round(v).toLocaleString(undefined),
      sorter: (a, b) => (a.prod_stat_netgen ?? 0) - (b.prod_stat_netgen ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: 'Change (MoM)',
      dataIndex: 'percent_change_net_units',
      key: 'percent_change_net_units',
      width: 135,
      render: formatPercentChange,
      sorter: (a, b) => (a.percent_change_net_units ?? 0) - (b.percent_change_net_units ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: 'Meter Read Type',
      dataIndex: 'meter_read_type',
      key: 'meter_read_type',
      width: 145,
      sorter: (a, b) => (a.meter_read_type ?? '').localeCompare(b.meter_read_type ?? ''),
      sortDirections: ['ascend', 'descend', null],
      render: (value: string | null, row: Row) => {
        const type = value?.toLowerCase();
        const showTooltip =
          (type === 'negative' || type === 'zero' || type?.includes('estimate')) &&
          !!row.comments;
        const tooltipText = showTooltip ? row.comments ?? undefined : undefined;
        const content = type?.includes('estimate') ? (
          <span
            style={{ color: '#1677ff', cursor: 'pointer' }}
            onClick={() => setSelectedMeter(row)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedMeter(row); }}
            data-testid="estimate-link"
            data-tooltip={tooltipText}
          >
            Estimate
          </span>
        ) : (
          <span data-tooltip={tooltipText}>{value}</span>
        );
        return showTooltip ? <Tooltip title={row.comments}>{content}</Tooltip> : content;
      },
    },
    {
      title: 'Approval Status',
      dataIndex: 'ready_for_approval',
      key: 'ready_for_approval',
      width: 210,
      sorter: (a, b) =>
        (a.ready_for_approval ?? 'In Progress').localeCompare(
          b.ready_for_approval ?? 'In Progress'
        ),
      sortDirections: ['ascend', 'descend', null],
      render: (_: unknown, row: Row) => {
        const isApproved = isRowOriginallyApproved(row.meter_computed_id);
        return (
          <Select
            size="small"
            value={row.ready_for_approval ?? 'In Progress'}
            style={{ width: 190 }}
            className={isApproved ? styles.approvedStatusSelect : undefined}
            popupMatchSelectWidth={false}
            disabled={isApproved}
            onChange={(val) => {
              handleEdit(row.meter_computed_id, 'ready_for_approval', val);
              if (val !== 'Ready for Approval') {
                handleEdit(row.meter_computed_id, 'approval_status', 'Rejected');
                handleEdit(row.meter_computed_id, 'prod_stat_approval_signature', '');
              }
            }}
            options={[
              { value: 'In Progress', label: 'In Progress' },
              { value: 'Ready for Approval', label: 'Ready for Approval' },
              ...(canApprove ? [{ value: 'Not Approved', label: 'Not Approved - Update Needed' }] : []),
              { value: 'Approved', label: 'Approved', disabled: true },
            ].map((o) => ({ ...o, 'data-testid': 'approval-status-option', 'data-value': toTestAttrValue(o.value) }))}
            data-testid="approval-status-select"
          />
        );
      },
    },
    {
      title: () => (
        <ApprovalColumnHeader
          canApprove={canApprove}
          checked={bulkHeaderChecked}
          indeterminate={bulkHeaderIndeterminate}
          disabled={bulkHeaderDisabled}
          onChange={handleBulkApproveHeaderChange}
        />
      ),
      key: 'approval_status',
      width: 280,
      render: (_: unknown, row: Row) => {
        const isPersisted = isRowOriginallyApproved(row.meter_computed_id);
        const canEditRow =
          canApprove && row.ready_for_approval === 'Ready for Approval' && !isPersisted;
        return (
          <ApprovalSignatureCell
            approved={row.approval_status === 'Approved'}
            signature={row.prod_stat_approval_signature}
            canEdit={canEditRow}
            username={username}
            onApprovedChange={(checked) => {
              handleEdit(
                row.meter_computed_id,
                'approval_status',
                checked ? 'Approved' : 'Rejected'
              );
              if (checked && !row.prod_stat_approval_signature && username) {
                handleEdit(row.meter_computed_id, 'prod_stat_approval_signature', username);
              }
            }}
            onSignatureChange={(val) =>
              handleEdit(row.meter_computed_id, 'prod_stat_approval_signature', val)
            }
          />
        );
      },
    },
  ];

  // data-column values for columns whose header is a render function
  const cellColumns = withTestColumnAttrs(columns, 'meter-value', { bulk_ready: 'Ready', approval_status: 'Approved' });

  return (
    <>
      <PageLayout
        title={`Prod Stat Dashboard for ${monthYearLabel} (Updated ${data?.length ?? 0} meters)`}
        headerAccessoryView={
          <div className={styles.periodSelector}>
            <ExportToExcelButton
              onClick={handleExportToExcel}
              style={{ marginRight: 12 }}
            />
            <NSPDatePicker
              picker="month"
              format="MMM YYYY"
              allowClear={false}
              value={pickerValue}
              onChange={handleDateChange}
              showNavigation={true}
              showCalendarIcon={true}
              onNavigateLeft={() => navigateMonth(-1)}
              onNavigateRight={() => navigateMonth(1)}
              isNextDisabled={isNextDisabled}
            />
          </div>
        }
        footerView={
          <NSPButton onClick={handleSaveWithConfirm} loading={saving} disabled={isSaveDisabled} data-testid="save-changes-button">
            Save Changes
          </NSPButton>
        }
      >
        <div className={styles.content}>
          {saveFeedback && (
            <Alert
              message={saveFeedback.status === 'success' ? 'Save Successful!' : 'Save Failed!'}
              description={saveFeedback.message}
              type={saveFeedback.status}
              showIcon
              closable
              style={{ marginBottom: 16 }}
              data-testid="save-feedback-alert"
              data-type={saveFeedback.status}
            />
          )}

          {asyncApproval && (
            <Alert
              message={
                asyncApproval.status === 'failed'
                  ? 'Approval Failed'
                  : asyncApproval.message
              }
              description={
                asyncApproval.status === 'failed'
                  ? asyncApproval.message
                  : <span style={{ display: 'none' }} />
              }
              type={
                asyncApproval.status === 'processing'
                  ? 'warning'
                  : asyncApproval.status === 'completed'
                    ? 'success'
                    : 'error'
              }
              showIcon
              closable
              onClose={() => setAsyncApproval(null)}
              style={{ marginBottom: 16 }}
              data-testid="approval-progress-alert"
              data-type={asyncApproval.status}
            />
          )}

          <Table<Row>
            dataSource={data}
            columns={cellColumns}
            loading={loading}
            rowKey="meter_computed_id"
            data-testid="prod-stat-table"
            data-loading={String(loading)}
            data-row-count={String(data?.length ?? 0)}
            onRow={(row) => ({
              'data-testid': 'meter-row',
              'data-meter-id': String(row.meter_id),
              'data-meter-number': row.meter_number ?? '',
              'data-meter-name': row.location ?? '',
              'data-vendor': row.vendor_name ?? '',
              'data-meter-read-type': toTestAttrValue(row.meter_read_type),
              'data-status': toTestAttrValue(row.ready_for_approval ?? 'In Progress'),
              'data-approved': String(row.approval_status === 'Approved'),
            }) as TestAttrs}
            pagination={false}
            scroll={{ x: 'max-content', y: 'calc(100vh - 280px)' }}
            tableLayout="fixed"
            sortDirections={['ascend', 'descend', null]}
          />
        </div>
      </PageLayout>

      <EstimationBlade
        open={bladeOpen}
        onClose={() => setSelectedMeter(null)}
        meter={selectedMeter}
        monthYear={bladeMonthYear}
        meterData={meterData}
        energySource={energySource}
        regionalData={regionalData}
        saving={bladeSaving}
        applyError={applyError}
        onApply={handleApply}
        onMonthChange={setBladeMonthYear}
        readOnly={selectedMeter ? isRowOriginallyApproved(selectedMeter.meter_computed_id) : false}
      />
    </>
  );
};

export default ProdStatDashboard;